/**
 * Sign-out and sign-in telemetry (ENG-4874).
 *
 * Until now signing out left no row, so a mid-session return to the login
 * screen could not be told apart from a chosen sign-out. `sign_out` carries a
 * `cause`: the person chose it, or their session ended. The cause is also kept
 * on this install so the next `sign_in` can carry `after_sign_out_cause`,
 * which is what lets Product derive "signed back in within 15 minutes" from
 * the pair.
 *
 * Three pieces of state, all storage-safe (a blocked localStorage must never
 * block sign-out):
 * - a per-document latch, so the one sign-out that fans out through the
 *   /logout route, the useAuth0 wrapper and the Electron `logout` IPC is
 *   recorded once;
 * - a pending cause, written by whatever detected an involuntary sign-out
 *   before it navigates to the /logout route, and read by that route;
 * - the last cause, written at sign-out and consumed by the next sign-in.
 *
 * Same injected-`track` shape as the sibling trackers (auth-journey.ts).
 * Per rule: reference-rules-in-comments (ai.knowledge/global); contract:
 * ENG-4874 "Event contract (Oct 28)".
 */

import type { EventCategory } from '../constants/event-categories.js';
import type { EventName } from '../constants/event-names.js';
import type { AnalyticsTrackFn } from './types.js';

// No runtime imports. This module is linked from electron/api.ts beside the
// constants barrel, and a second concurrent link of event-names.ts trips the
// jest --experimental-vm-modules linker in every suite that resets the
// registry around api.ts ("Module cache already has entry"). The names are
// literals checked against the catalog types at the track() call, so a name
// missing from EVENT_NAMES still fails tsc, and the emit-site scanner reads
// a literal directly. Per knowledge: esm-jest-mock-patterns (ai.knowledge/global).
const SIGN_OUT_EVENT = 'sign_out';
const SIGN_IN_EVENT = 'sign_in';
const AUTH_CATEGORY = 'auth';
const assertCatalogued: (name: EventName, category: EventCategory) => void = () => {};
assertCatalogued(SIGN_OUT_EVENT, AUTH_CATEGORY);
assertCatalogued(SIGN_IN_EVENT, AUTH_CATEGORY);

export const SIGN_OUT_CAUSE = {
  /** The person chose it. */
  USER: 'user',
  /** The app signed them out on its own. */
  SESSION_ENDED: 'session_ended'
} as const;

export type SignOutCause = (typeof SIGN_OUT_CAUSE)[keyof typeof SIGN_OUT_CAUSE];

// Same `analytics.` namespace as `analytics.last_known_user_id` (auth/auth-journey.ts).
const LAST_SIGN_OUT_CAUSE_KEY = 'analytics.last_sign_out_cause';
const PENDING_SIGN_OUT_CAUSE_KEY = 'analytics.pending_sign_out_cause';

let signOutTrackedInDocument = false;

const safeLocalStorage = (): Storage | undefined =>
  typeof window !== 'undefined' ? window.localStorage : undefined;

const readCause = (key: string): SignOutCause | undefined => {
  try {
    const value = safeLocalStorage()?.getItem(key);
    return isSignOutCause(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeCause = (key: string, cause: SignOutCause): void => {
  try {
    safeLocalStorage()?.setItem(key, cause);
  } catch {
    // Tracking storage must never block sign-out.
  }
};

const removeCause = (key: string): void => {
  try {
    safeLocalStorage()?.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

export const isSignOutCause = (value: unknown): value is SignOutCause =>
  value === SIGN_OUT_CAUSE.USER || value === SIGN_OUT_CAUSE.SESSION_ENDED;

/**
 * Leave a cause for the sign-out that is about to happen. For detectors that
 * end the session by navigating to the /logout route (a full navigation, so
 * no in-memory state survives): the route reads it back with
 * {@link takePendingSignOutCause} and records the sign-out with that cause
 * instead of assuming the person chose it.
 */
export const markSignOutCause = (cause: SignOutCause): void => {
  writeCause(PENDING_SIGN_OUT_CAUSE_KEY, cause);
};

/** Read and clear the cause a detector left for this sign-out, if any. */
export const takePendingSignOutCause = (): SignOutCause | undefined => {
  const cause = readCause(PENDING_SIGN_OUT_CAUSE_KEY);
  removeCause(PENDING_SIGN_OUT_CAUSE_KEY);
  return cause;
};

/** Whether this document has already recorded its sign-out. */
export const wasSignOutTracked = (): boolean => signOutTrackedInDocument;

/**
 * Record the sign-out once per document. Returns false when it was already
 * recorded, so every path a single sign-out fans out through can call this
 * without producing a second row. Call it BEFORE the PostHog reset, so the row
 * still belongs to the person.
 */
export const trackSignOut = (
  track: AnalyticsTrackFn,
  { cause }: { cause: SignOutCause }
): boolean => {
  if (signOutTrackedInDocument) return false;
  signOutTrackedInDocument = true;

  // A pending cause belongs to this sign-out whether or not the caller read
  // it; never let it label a later, unrelated sign-out.
  removeCause(PENDING_SIGN_OUT_CAUSE_KEY);
  writeCause(LAST_SIGN_OUT_CAUSE_KEY, cause);
  track(SIGN_OUT_EVENT, AUTH_CATEGORY, { cause });
  return true;
};

/**
 * Record the sign-in, paired with the sign-out that preceded it on this
 * install when there was one. Consumes the stored cause so a later sign-in
 * with no sign-out between (an account switch) carries nothing.
 */
export const trackSignIn = (track: AnalyticsTrackFn): void => {
  const afterSignOutCause = readCause(LAST_SIGN_OUT_CAUSE_KEY);
  removeCause(LAST_SIGN_OUT_CAUSE_KEY);
  // A detector's note that never reached a sign-out is stale once signed in.
  removeCause(PENDING_SIGN_OUT_CAUSE_KEY);
  track(
    SIGN_IN_EVENT,
    AUTH_CATEGORY,
    afterSignOutCause !== undefined ? { after_sign_out_cause: afterSignOutCause } : {}
  );
};

/** Test seam: a fresh document has not recorded a sign-out. */
export const __resetSignOutTrackingForTests = (): void => {
  signOutTrackedInDocument = false;
};
